import { useEffect, useState, type CSSProperties } from 'react';
import { Pestanas } from '../components/Pestanas';
import { TopBar } from '../components/TopBar';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { avisar, confirmar, textoDeError } from '../components/Dialogo';
import { usePermisos } from '../lib/permisos';
import { RecorridoCita } from '../components/RecorridoCita';

interface Window { window_id: string; match_pattern: string; window_days: number; label: string; activo: boolean }


export function Config() {
  const [windows, setWindows] = useState<Window[]>([]);
  // La pestaña puede venir en la liga (`/config?tab=datos`), como en Administración y App de pacientes.
  const [tab, setTab] = useState<'asig' | 'recorrido' | 'win' | 'datos'>(() => { const t = new URLSearchParams(window.location.search).get('tab'); return t === 'win' || t === 'datos' || t === 'recorrido' ? t : 'asig'; });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ windows: Window[] }>('crm/config')
      .then((r) => setWindows(r.windows))
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div>
      <TopBar />
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '18px 16px 48px' }}>
        <h1 style={{ margin: '4px 2px 8px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Reglas y datos</h1>
        <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>De dónde sale quién atiende a cada persona, cuándo toca la recompra y de qué están hechos los números.</p>

        {/* Los guiones de seguimiento (antes "Mensajes") se fueron con Mi día (0.40.0): el equipo escribe desde Halory. */}
        <Pestanas items={[['asig', 'Asignación de leads'], ['recorrido', 'Recorrido de la cita'], ['win', 'Ventanas de recompra'], ['datos', 'Salud de datos']] as const} valor={tab} onCambio={setTab} etiqueta="Secciones de reglas y datos" estilo={{ margin: '8px 0 18px' }} />

        {error && <div className="card" style={{ padding: 16, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{error}</div>}

        {tab === 'asig' && <AsignacionLeads />}
        {tab === 'recorrido' && <RecorridoCita />}
        {tab === 'win' && (
          <div style={{ display: 'grid', gap: 8 }}>
            <div className="muted" style={{ fontSize: 13 }} data-ventanas-nota>
              Días que pasan, por tipo de tratamiento, antes de sugerir la recompra. Son las MISMAS ventanas de <Link to="/app-pacientes?tab=recordatorios" style={{ color: 'var(--gold-dark)', fontWeight: 600 }}>App de pacientes → Recordatorios</Link> (ahí además se decide si se le avisa en su app): cambiar una aquí la cambia allá.
            </div>
            {windows.map((w) => <WindowRow key={w.window_id} w={w} />)}
          </div>
        )}
        {tab === 'datos' && <SaludDatos />}
      </div>
    </div>
  );
}

interface Asignacion {
  fuente: 'halory'; configurado: boolean;
  sync: { ultimo: string | null; motivo: string | null; reasignados: number | null; altas: number | null; nuevas?: number | null };
  sinCuenta: { nombre: string; leads: number }[]; sinAsignar: number | null;
  cuentas: { nombre: string; alias: string[] | null }[];
  /** Quién está hoy en el equipo de Halory (lo guarda el reloj) y si además tiene cuenta en el HUB (0.42.0). */
  equipoHalory: { nombre: string; cuenta: boolean }[]; equipoFuente: 'usuarios' | 'leads' | null;
  /** 0.47.0: lo que llega al momento (eventos firmados de Halory)… */
  eventos: { ultimo: string | null; tipo: string | null; hoy: number; webhook: boolean; leadUpdated: string | null } | null;
  /** …y la red de seguridad: la revisión completa de la noche (o la de "Revisar ahora"). */
  conciliacion: { ultimo?: string; via?: 'noche' | 'manual' | 'primera'; motivo?: string | null; leidos?: number; altas?: number; reasignados?: number; vinculados?: number; renombrados?: number; nuevas?: number;
    /** 0.54.0: las citas de los dos lados. */
    citas?: { ultimo?: string; leidas: number; faltaban: number; espejadas: number; pendientes: number; corregidas: number; errores: number; completa: boolean; motivo?: string | null } } | null;
  pauta: { ultimo: string | null; recienteAt: string | null; motivo: string | null; noche: { dia?: string; hasta?: string; completo?: boolean; at?: string } | null; horaDias: number; nocheDias: number } | null;
  /** DTB → Halory: el interruptor y cómo va. */
  haloryAlta: { crear_en_halory: boolean; updated_at: string | null; updated_by: string | null; enviados: number; errores: number; detenidos: number; ultimo: string | null; ultimoError: string | null; hooks: boolean } | null;
  exportar: { url: string; firmado: boolean } | null;
  /** 0.52.0: el padrón de la clínica en Halory. */
  padron: Padron | null;
}

/** Cómo va el padrón en Halory (api `lib/halory-padron.ts` → `resumenPadron`). Los números son de la foto EN VIVO. */
interface Padron {
  activo: boolean; configurado: boolean; estado: 'cargando' | 'al_dia';
  version: number; v: number | null; pasada: 'version' | 'noche' | null; inicio: string | null; fin: string | null;
  enviados: number; creados: number; ligados: number;
  total: number | null; enHalory: number | null; faltan: number | null; listos: number | null; errores: number | null; enEspera: number | null;
  sinContacto: number | null; correoDescartado: number | null; ambiguos: number | null; ligadosAOtro: number | null; ilegibles: number | null;
  motivoError: string | null; fotoAt: string | null;
  ultimaTanda: { at: string; via: string; pasada: string; filas: number; ms: number; enviados: number; creados: number; ligados: number; ambiguos: number; ligadosAOtro: number; errores: number; motivo: string | null } | null;
  ultimaVuelta: string | null; motivo: string | null; esperaHasta: string | null;
}

const num = (n: number | null | undefined) => (n ?? 0).toLocaleString('es-MX');
const plural = (n: number | null | undefined, uno: string, varios: string) => `${num(n)} ${(n ?? 0) === 1 ? uno : varios}`;
/** "Citas: 77 revisadas, 2 corregidas" (0.54.0): lo de la conciliación de citas, en una línea. */
const citasLinea = (c: { leidas: number; faltaban: number; espejadas: number; pendientes: number; corregidas: number; errores: number }) =>
  [`${num(c.leidas)} ${c.leidas === 1 ? 'revisada' : 'revisadas'}`, `${num(c.corregidas + c.espejadas)} ${c.corregidas + c.espejadas === 1 ? 'corregida' : 'corregidas'}`,
    c.espejadas ? `${num(c.espejadas)} que faltaba${c.espejadas === 1 ? '' : 'n'} y ya ${c.espejadas === 1 ? 'está' : 'están'} en DTB` : '',
    c.pendientes ? `${num(c.pendientes)} sin paciente (Analítica → Citas)` : '', c.errores ? plural(c.errores, 'con error', 'con error') : '']
    .filter(Boolean).join(', ');

const cuandoTxt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : null);
/** Cierra una frase con punto sin duplicarlo (las horas ya terminan en "a.m." / "p.m."). */
const punto = (t: string) => (/[.!?]$/.test(t.trim()) ? '' : '.');
const VIA: Record<string, string> = { noche: 'la de la noche', manual: 'a mano («Revisar ahora»)', primera: 'la primera' };
const EVENTO: Record<string, string> = { 'lead.created': 'lead nuevo', 'lead.updated': 'cambio en un lead', 'message.received': 'mensaje recibido', 'message.sent': 'mensaje enviado',
  'appointment.created': 'cita nueva', 'appointment.updated': 'cambio en una cita', 'appointment.canceled': 'cita cancelada' };

/**
 * Quién atiende a cada persona y cómo se entera el HUB (0.47.0). Lo decide HALORY y el HUB lo copia AL MOMENTO: Halory
 * avisa cada cambio con un evento firmado (lead nuevo, lead que cambia de manos, citas). El reloj ya no le pregunta cada
 * 10 minutos: una revisión completa cada noche (12–6 a.m.) es la red de seguridad, y «Revisar ahora» la corre en el
 * momento. Aquí también: dar de alta en Halory a quien se registra en DTB (apagado de inicio), la URL para la carga
 * inicial de Halory y cada cuánto llega el gasto de pauta.
 */
function AsignacionLeads() {
  // Reglas y datos se abre por sección (Administración → Permisos); mandar datos de pacientes a Halory es de admin.
  const { esAdmin } = usePermisos();
  const [d, setD] = useState<Asignacion | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [msg, setMsg] = useState<{ t: string; ok: boolean } | null>(null);
  const [copiado, setCopiado] = useState(false);
  // Una respuesta incompleta (una versión vieja del servidor) no tumba la pantalla: se completa con vacíos.
  const cargar = () => api<Partial<Asignacion>>('crm/asignacion').then((r) => setD({
    fuente: 'halory', configurado: r.configurado !== false, sinAsignar: r.sinAsignar ?? null,
    sync: { ultimo: r.sync?.ultimo ?? null, motivo: r.sync?.motivo ?? null, reasignados: r.sync?.reasignados ?? null, altas: r.sync?.altas ?? null, nuevas: r.sync?.nuevas ?? null },
    sinCuenta: Array.isArray(r.sinCuenta) ? r.sinCuenta : [], cuentas: Array.isArray(r.cuentas) ? r.cuentas : [],
    equipoHalory: Array.isArray(r.equipoHalory) ? r.equipoHalory : [], equipoFuente: r.equipoFuente ?? null,
    eventos: r.eventos ?? null, conciliacion: r.conciliacion ?? null, pauta: r.pauta ?? null, haloryAlta: r.haloryAlta ?? null, exportar: r.exportar ?? null,
    padron: r.padron ?? null,
  })).catch((e) => setErr(textoDeError(e)));
  useEffect(() => { cargar(); }, []);
  // Mientras el padrón se está cargando, su tarjeta (y sólo ella) se refresca cada 20 s con la pantalla a la vista: la
  // carga la hace el reloj y el avance se ve sin recargar. El resto de la pantalla no se toca.
  const cargandoPadron = !!d?.padron?.activo && d.padron.estado === 'cargando';
  useEffect(() => {
    if (!cargandoPadron) return;
    const t = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      api<Padron>('crm/asignacion/padron').then((p) => setD((x) => (x ? { ...x, padron: p } : x))).catch(() => { /* la siguiente vuelta */ });
    }, 20_000);
    return () => window.clearInterval(t);
  }, [cargandoPadron]);
  if (err) return <div className="card" style={{ padding: 16, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{err}</div>;
  if (!d) return <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>;

  const revisar = async () => {
    setRevisando(true); setMsg(null);
    try {
      const r = await api<any>('crm/asignacion/revisar', { method: 'POST', body: '{}' });
      const c = r.conciliacion || {};
      const ci = c.citas;
      const citasTxt = ci ? ` Citas: ${citasLinea(ci)}${ci.motivo ? ` (${ci.motivo})` : ''}.` : '';
      setMsg(c.motivo ? { t: `No se pudo terminar: ${c.motivo}${citasTxt}`, ok: false }
        : { t: `Listo: se revisaron ${c.leidos ?? 0} leads de Halory · ${c.altas ?? 0} alta${c.altas === 1 ? '' : 's'} nueva${c.altas === 1 ? '' : 's'} · ${c.reasignados ?? 0} cambio${c.reasignados === 1 ? '' : 's'} de quién atiende.${citasTxt}`, ok: true });
      cargar();
    } catch (e) { setMsg({ t: textoDeError(e), ok: false }); }
    setRevisando(false);
  };
  const interruptor = async (si: boolean) => {
    if (si && !(await confirmar({ titulo: '¿Dar de alta en Halory a quien se registra en DTB?', confirmar: 'Prender',
      mensaje: `Cada expediente nuevo de Dra. IA, recepción o la app se crea también como contacto en Halory (con su nombre de verdad, teléfono y correo), ligado por su número de expediente. Si Halory ya tiene a esa persona, sólo se ligan. Si encuentra varios contactos posibles no adivina: queda en Alertas → Revisar registros.\n\nTambién se manda lo que YA estaba${d?.padron?.faltan ? `: ${plural(d.padron.faltan, 'expediente que falta', 'expedientes que faltan')}` : ''}, por tandas, en las siguientes vueltas del reloj (sin hacer nada más).` }))) return;
    if (!si && !(await confirmar({ titulo: '¿Apagar el alta en Halory?', confirmar: 'Apagar', peligro: true,
      mensaje: 'Los registros nuevos de DTB ya no se crean en Halory. Lo que ya está ligado se queda ligado, y la marca de cuenta de prueba sigue llegando a Halory.' }))) return;
    try {
      await api('crm/asignacion/halory', { method: 'PUT', body: JSON.stringify({ crear_en_halory: si }) });
      setMsg({ t: si ? 'Prendido: lo que se registre en DTB desde ahora llega a Halory, y lo que ya estaba se manda por tandas.' : 'Apagado: los registros nuevos ya no se crean en Halory.', ok: true });
      cargar();
    } catch (e) { await avisar({ titulo: 'No se pudo cambiar', mensaje: textoDeError(e), tipo: 'error' }); }
  };
  const copiar = async (t: string) => { try { await navigator.clipboard.writeText(t); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { setCopiado(false); } };

  const ev = d.eventos; const co = d.conciliacion; const pa = d.pauta; const ha = d.haloryAlta;
  const filaCss = { padding: '12px 14px', fontSize: 13, lineHeight: 1.55 } as const;
  return (
    <div data-asignacion style={{ display: 'grid', gap: 10 }}>
      <div className="card" style={{ ...filaCss, fontSize: 13.5 }}>
        <strong>Quién atiende a cada persona lo decide Halory.</strong> Cuando llega un lead nuevo queda a nombre de quien lo
        atiende allá, y si Halory lo reasigna después, Halory le avisa al HUB en el momento y se copia solo. No hay nada que
        configurar aquí: si quieres cambiar el reparto, se cambia en Halory. Tampoco hace falta que tengan cuenta en el HUB:
        quien atiende en Halory cuenta igual (la cuenta sólo sirve para entrar aquí).
      </div>
      {msg && <div role="status" className="card" data-asignacion-msg={msg.ok ? 'ok' : 'error'} style={{ ...filaCss, border: 'none', background: msg.ok ? 'var(--success-bg)' : 'var(--warning-bg)', color: msg.ok ? 'var(--success)' : 'var(--warning)' }}>{msg.t}</div>}

      <div className="card" style={filaCss} data-asignacion-eventos>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>Al momento: los avisos de Halory</div>
        {ev && !ev.webhook
          ? <span style={{ color: 'var(--warning)' }}>Falta el secreto del webhook de Halory (<code>HALORY_WEBHOOK_SECRET</code>): sin él los avisos no se aceptan y sólo queda la revisión de la noche.</span>
          : ev && ev.ultimo
            ? <>Último aviso: <strong>{cuandoTxt(ev.ultimo)}</strong>{ev.tipo ? ` (${EVENTO[ev.tipo] || ev.tipo})` : ''} · {ev.hoy} hoy
                {ev.leadUpdated ? <> · último cambio de un lead: {cuandoTxt(ev.leadUpdated)}</> : null}{ev.leadUpdated ? punto(cuandoTxt(ev.leadUpdated) || '') : '.'}</>
            : <span className="muted">Todavía no llega ningún aviso de Halory. En Halory → Integraciones / API, el webhook de DTB tiene que estar suscrito a lead.created, lead.updated, message.received y appointment.*.</span>}
      </div>

      <div className="card" style={filaCss} data-asignacion-conciliacion>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600 }}>Red de seguridad: la revisión completa</div>
          <button className="btn-ghost" style={{ padding: '4px 12px', fontSize: 13 }} disabled={revisando || !d.configurado} onClick={revisar} data-revisar-ahora>{revisando ? 'Revisando…' : 'Revisar ahora'}</button>
        </div>
        <div className="muted" style={{ fontSize: 12.5, margin: '2px 0 4px' }}>Una vez cada noche (entre las 12 y las 6 a.m.) se compara todo con Halory por si algún aviso no llegó: quién atiende, leads que faltan, nombres, el equipo y las citas (las que faltan aquí y las que cambiaron allá). «Revisar ahora» la corre en este momento (puede tardar unos segundos).</div>
        {!d.configurado
          ? <span style={{ color: 'var(--warning)' }}>Falta la llave de Halory (<code>HALORY_API_KEY</code>): sin ella no hay a quién preguntarle, y sólo cuentan los avisos.</span>
          : co && co.ultimo
            ? <span data-conciliacion-ultima>Última: <strong>{cuandoTxt(co.ultimo)}</strong> ({VIA[co.via || ''] || co.via})
                {co.motivo ? <span style={{ color: 'var(--warning)' }}> · no se pudo: {co.motivo}</span>
                  : <> · {co.leidos ?? 0} {co.leidos === 1 ? 'lead revisado' : 'leads revisados'} · {co.altas ?? 0} alta{co.altas === 1 ? '' : 's'} · {co.reasignados ?? 0} cambio{co.reasignados === 1 ? '' : 's'} de quién atiende{co.renombrados ? ` · ${co.renombrados} con su nombre` : ''}{co.nuevas ? ` · ${co.nuevas} persona${co.nuevas === 1 ? '' : 's'} de Halory nueva${co.nuevas === 1 ? '' : 's'} en la lista` : ''}</>}.</span>
            : <span className="muted">Todavía no corre: la primera es en la siguiente vuelta del reloj, o tócala ahora.</span>}
        {d.configurado && co?.citas && <div style={{ marginTop: 4 }} data-conciliacion-citas>
          Citas: {citasLinea(co.citas)}{co.citas.motivo ? <span style={{ color: 'var(--warning)' }}> · {co.citas.completa ? '' : 'quedó a medias: '}{co.citas.motivo}</span> : null}.
        </div>}
        {d.sinAsignar != null && <div style={{ marginTop: 4 }} data-sin-asignar>Personas que llegaron por Halory sin nadie asignado todavía: <strong>{d.sinAsignar}</strong>.</div>}
      </div>

      <div className="card" style={filaCss} data-asignacion-pauta>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>Gasto de pauta (Meta, por Halory)</div>
        <div className="muted" style={{ fontSize: 12.5 }}>Cada hora se traen los últimos {pa?.horaDias ?? 2} días (Meta los reajusta) y cada noche los últimos {pa?.nocheDias ?? 28}. Traerlos otra vez corrige, no suma.</div>
        {pa && (pa.recienteAt || pa.motivo) && <div style={{ marginTop: 4 }}>
          {pa.recienteAt ? <>Lo de los últimos días se trajo el <strong>{cuandoTxt(pa.recienteAt)}</strong>{punto(cuandoTxt(pa.recienteAt) || '')} </> : null}
          {pa.noche?.at ? <>Los {pa.nocheDias} días: {pa.noche.completo ? `completos la noche del ${new Date(pa.noche.at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}` : `a la mitad (hasta ${pa.noche.hasta || '…'}), sigue en la siguiente vuelta`}. </> : null}
          {pa.motivo ? <span style={{ color: 'var(--warning)' }}>No se pudo: {pa.motivo}</span> : null}
        </div>}
      </div>

      {ha && (
        <div className="card" style={filaCss} data-asignacion-alta>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input type="checkbox" checked={ha.crear_en_halory} disabled={!esAdmin} title={esAdmin ? undefined : 'Lo prende o lo apaga sólo una cuenta de admin'} onChange={(e) => interruptor(e.target.checked)} style={{ width: 'auto', marginTop: 3 }} data-crear-en-halory />
            <span><strong>Dar de alta en Halory a quien se registra en DTB</strong>
              <span className="muted" style={{ display: 'block', fontSize: 12.5 }}>Cada expediente nuevo de Dra. IA, recepción o la app se crea también como contacto en Halory, con su nombre, teléfono y correo, ligado por su número de expediente. Al prenderlo se manda también lo que ya estaba (todo el padrón, por tandas). Si Halory encuentra varios contactos posibles no adivina: queda en Alertas → Revisar registros. Apagado de inicio.</span></span>
          </label>
          <div style={{ marginTop: 6 }} data-alta-estado>
            {ha.crear_en_halory ? <>Prendido{ha.updated_by ? ` por ${ha.updated_by}` : ''}{ha.updated_at ? ` el ${cuandoTxt(ha.updated_at)}` : ''}{ha.updated_at ? punto(cuandoTxt(ha.updated_at) || '') : '.'} </> : 'Apagado. '}
            {ha.enviados || ha.errores || ha.detenidos
              ? <>Enviados: <strong>{ha.enviados}</strong>{ha.ultimo ? ` (el último, ${cuandoTxt(ha.ultimo)})` : ''}{ha.detenidos ? <> · {ha.detenidos} esperan a una persona (<Link to="/alertas?sec=registros" style={{ color: 'var(--gold-dark)' }}>Revisar registros</Link>)</> : null}
                {ha.errores ? <span style={{ color: 'var(--warning)' }}> · {ha.errores} con error{ha.ultimoError ? `: ${ha.ultimoError}` : ''} (se reintentan solos)</span> : null}.</>
              : null}
          </div>
          {!esAdmin && <div className="muted" style={{ marginTop: 4, fontSize: 12.5 }} data-solo-admin="alta-halory">Prenderlo o apagarlo lo hace sólo una cuenta de admin: manda datos de pacientes a otra empresa.</div>}
          {!ha.hooks && <div style={{ marginTop: 4, color: 'var(--warning)' }}>Dra. IA y recepción todavía no avisan cuando registran a alguien: falta <code>HUB_HOOK_SECRET</code> aquí (y el mismo valor con <code>HUB_NOTIFY_URL</code> en sus App Settings).</div>}
        </div>
      )}

      {d.padron && <PadronHalory p={d.padron} filaCss={filaCss} puedeMandar={esAdmin} onCambio={(p) => setD((x) => (x ? { ...x, padron: p } : x))} />}

      {d.exportar && (
        <div className="card" style={filaCss} data-asignacion-exportar>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>Carga inicial en Halory</div>
          <div className="muted" style={{ fontSize: 12.5 }}>Para que Halory tenga a todos los expedientes de DTB desde el principio: en Halory → Integraciones / API → el endpoint de DTB, pega esta dirección y usa «Importar contactos» (enseña una vista previa antes). Halory la pide firmada con el mismo secreto de su webhook; sólo salen los que tienen teléfono o correo.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
            <code style={{ fontSize: 12, background: 'var(--bg-alt)', padding: '4px 8px', borderRadius: 8, overflowWrap: 'anywhere', minWidth: 0 }} data-exportar-url>{d.exportar.url}</code>
            <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12.5 }} onClick={() => copiar(d.exportar!.url)} data-copiar-url>{copiado ? 'Copiada' : 'Copiar'}</button>
          </div>
          {!d.exportar.firmado && <div style={{ marginTop: 4, color: 'var(--warning)' }}>Falta <code>HALORY_WEBHOOK_SECRET</code>: sin él la dirección contesta que no está configurada.</div>}
        </div>
      )}

      {d.sinCuenta.length > 0 && (
        <div className="card" style={{ ...filaCss, background: 'var(--warning-bg)', color: 'var(--warning)', border: 'none' }} data-asignacion-sin-cuenta>
          <strong>No se pudieron agregar a la lista:</strong> {d.sinCuenta.map((x) => `${x.nombre} (${x.leads})`).join(', ')}.
          Se vuelve a intentar en cada revisión de Halory.
        </div>
      )}
      {d.equipoHalory.length > 0 && (
        <div className="card" style={filaCss} data-asignacion-equipo>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Equipo de Halory hoy</div>
          <div className="muted" style={{ marginBottom: 6 }}>{d.equipoFuente === 'leads'
            ? 'La llave de Halory no tiene el permiso users:read: se sabe por quién atiende leads.'
            : 'Los usuarios de Halory. Todos cuentan en Equipo; la cuenta del HUB sólo hace falta para entrar aquí.'}</div>
          {d.equipoHalory.map((x) => (
            <div key={x.nombre} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '4px 0', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 600 }}>{x.nombre}</span>
              <span className="muted">{x.cuenta ? 'con cuenta en el HUB' : 'sólo en Halory'}</span>
            </div>
          ))}
        </div>
      )}
      <div className="card" style={filaCss} data-asignacion-cuentas>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Cuentas de ventas / asesoría y cómo las reconoce</div>
        {d.cuentas.length === 0 && <div className="muted">No hay cuentas de ventas / asesoría activas.</div>}
        {d.cuentas.map((c) => (
          <div key={c.nombre} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '4px 0', borderTop: '1px solid var(--border)' }}>
            <span style={{ fontWeight: 600 }}>{c.nombre}</span>
            <span className="muted">{c.alias && c.alias.length ? `empata con: ${c.alias.join(', ')}` : 'sin nombres para empatar (vuelve a guardar la cuenta en Administración)'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * El padrón de la clínica en Halory (0.52.0): cuántos expedientes ya están allá, la barra mientras se carga, por qué no se
 * pueden mandar los demás y la última tanda. La carga la hace el reloj; «Mandar ahora» corre una tanda en el momento.
 */
function PadronHalory({ p, filaCss, onCambio, puedeMandar }: { p: Padron; filaCss: CSSProperties; onCambio: (p: Padron) => void; puedeMandar: boolean }) {
  const [mandando, setMandando] = useState(false);
  const [msg, setMsg] = useState<{ t: string; ok: boolean } | null>(null);
  const mandables = (p.enHalory ?? 0) + (p.faltan ?? 0);
  const pct = mandables ? Math.round(((p.enHalory ?? 0) / mandables) * 1000) / 10 : 100;
  const detenidos = (p.ambiguos ?? 0) + (p.ligadosAOtro ?? 0);
  const t = p.ultimaTanda;
  const mandar = async () => {
    setMandando(true); setMsg(null);
    try {
      const r = await api<{ ok: boolean; tanda: any; padron: Padron }>('crm/asignacion/padron', { method: 'POST', body: '{}' });
      const x = r.tanda || {};
      setMsg(x.motivo && !x.enviados ? { t: `No se pudo mandar: ${x.motivo}${punto(String(x.motivo))}`, ok: false }
        : !x.filas ? { t: 'No había nadie por mandar en este momento.', ok: true }
        : { t: `Listo: se mandaron ${num(x.filas)} · ${plural(x.creados, 'nuevo', 'nuevos')} en Halory · ${plural(x.ligados, 'ya existía', 'ya existían')}${x.ambiguos + x.ligadosAOtro ? ` · ${num(x.ambiguos + x.ligadosAOtro)} a Revisar registros` : ''}${x.errores ? ` · ${plural(x.errores, 'con error', 'con error')}` : ''}.`, ok: true });
      if (r.padron) onCambio(r.padron);
    } catch (e) { setMsg({ t: textoDeError(e), ok: false }); }
    setMandando(false);
  };
  return (
    <div className="card" style={filaCss} data-padron data-padron-estado={p.activo ? p.estado : 'apagado'}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600 }}>Padrón en Halory</div>
        {puedeMandar && <button className="btn-ghost" style={{ padding: '4px 12px', fontSize: 13 }} disabled={mandando || !p.activo || !p.configurado} onClick={mandar} data-padron-mandar>{mandando ? 'Mandando…' : 'Mandar ahora'}</button>}
      </div>
      <div style={{ fontSize: 14, margin: '4px 0 6px' }} data-padron-cuantos>
        <strong>{num(p.enHalory)}</strong> de <strong>{num(p.total)}</strong> expedientes ya están en Halory.
      </div>
      {p.activo && p.configurado && (
        <div style={{ margin: '0 0 6px' }}>
          <div role="progressbar" aria-label="Avance de la carga del padrón en Halory" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
            style={{ height: 8, borderRadius: 999, background: 'var(--bg-alt)', overflow: 'hidden' }} data-padron-barra>
            <div style={{ width: `${pct}%`, height: '100%', background: p.estado === 'al_dia' ? 'var(--success)' : 'var(--gold)', transition: 'width .4s' }} />
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }} data-padron-avance>
            {p.estado === 'al_dia'
              ? (p.faltan ?? 0) > 0
                ? `Al día: lo demás ya está en Halory; ${p.faltan === 1 ? 'falta 1, que se vuelve a intentar solo' : `faltan ${num(p.faltan)}, que se vuelven a intentar solos`} (el porqué, abajo).`
                : 'Al día: todo lo que se puede mandar ya está en Halory.'
              : <>Cargando: {pct.toLocaleString('es-MX')} % de lo que se puede mandar · faltan {num(p.faltan)}{p.enEspera ? ` (${num(p.enEspera)} esperan tras un error)` : ''}. Lo manda el reloj por tandas; esta tarjeta se actualiza sola.</>}
          </div>
        </div>
      )}
      {!p.activo && <div className="muted" style={{ marginBottom: 4 }} data-padron-apagado>Apagado: no se manda nada. Al prender «Dar de alta en Halory a quien se registra en DTB» se manda también todo lo que ya estaba.</div>}
      {p.activo && !p.configurado && <div style={{ color: 'var(--warning)', marginBottom: 4 }}>Falta la llave de Halory (<code>HALORY_API_KEY</code>): sin ella no hay a dónde mandar.</div>}
      <div style={{ display: 'grid', gap: 2 }}>
        {(p.sinContacto ?? 0) > 0 && <div data-padron-sin-contacto>{plural(p.sinContacto, 'expediente', 'expedientes')} sin teléfono ni correo: no se pueden mandar (en Halory no habría cómo encontrarlos){p.correoDescartado ? `; de ésos, ${num(p.correoDescartado)} ${p.correoDescartado === 1 ? 'trae' : 'traen'} un correo que Halory no acepta` : ''}.</div>}
        {detenidos > 0 && <div data-padron-detenidos>{plural(detenidos, 'expediente', 'expedientes')} con varias coincidencias en Halory (o con un contacto que ya es de otro expediente): <Link to="/alertas?sec=registros" style={{ color: 'var(--gold-dark)' }}>Revisar registros</Link>.</div>}
        {(p.errores ?? 0) > 0 && <div style={{ color: 'var(--warning)' }} data-padron-errores>{plural(p.errores, 'expediente', 'expedientes')} con error{p.motivoError ? `: ${p.motivoError}` : ''}{/se vuelve/i.test(p.motivoError || '') ? '' : ` (${p.errores === 1 ? 'se vuelve a intentar solo' : 'se vuelven a intentar solos'})`}.</div>}
        {(p.ilegibles ?? 0) > 0 && <div style={{ color: 'var(--warning)' }}>{plural(p.ilegibles, 'expediente', 'expedientes')} con el contacto cifrado con otra llave: no se pueden leer ni mandar.</div>}
      </div>
      {t && (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }} data-padron-tanda>
          Última tanda: {cuandoTxt(t.at)}{t.motivo
            ? <span style={{ color: 'var(--warning)' }}> · no se pudo: {t.motivo}{p.esperaHasta ? ` · se vuelve a intentar sola después de las ${new Date(p.esperaHasta).toLocaleTimeString('es-MX', { timeStyle: 'short' })}` : ''}</span>
            : <> · {plural(t.filas, 'expediente', 'expedientes')}: {plural(t.creados, 'nuevo', 'nuevos')} en Halory, {plural(t.ligados, 'ya existía', 'ya existían')}{t.ambiguos + t.ligadosAOtro ? `, ${num(t.ambiguos + t.ligadosAOtro)} a Revisar registros` : ''}{t.errores ? `, ${num(t.errores)} con error` : ''}.</>}
        </div>
      )}
      {!t && p.motivo && <div style={{ color: 'var(--warning)', fontSize: 12.5, marginTop: 6 }}>No se pudo: {p.motivo}</div>}
      <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
        {p.pasada === 'version' ? 'La carga completa va a la mitad; sigue sola. ' : p.fin ? `La última revisión completa terminó el ${cuandoTxt(p.fin)}${punto(cuandoTxt(p.fin) || '')} ` : ''}
        Cada noche se revisa otra vez todo el padrón, y en cada vuelta del reloj lo de los últimos 7 días: lo que el aviso de un alta no alcanzó a mandar, llega igual.
      </div>
      {msg && <div role="status" data-padron-msg={msg.ok ? 'ok' : 'error'} style={{ marginTop: 6, color: msg.ok ? 'var(--success)' : 'var(--warning)' }}>{msg.t}</div>}
    </div>
  );
}

interface Salud {
  volumen: { t: string; n: number }[];
  rangosDeFecha: { campo: string; desde: string | null; hasta: string | null; n: number }[];
  telefonos: {
    conTelefonoUsable: number; sinTelefono: number; ilegible: number;
    numerosCompartidos: number; pacientesAfectados: number;
    detalle: { telefono: string; pacientes: number; nombres: string[]; ids: string[]; estados: (string | null)[] }[];
  };
  nombresRepetidos: { nombre: string; casos: { id: string; status: string | null; asesora: string | null; alta: string; origen: string | null; citas: number }[] }[];
  origenDeCitas: { origen: string; n: number }[];
  halory: { pacientesCreadosDesdeHalory: number; nota: string };
  advertencia: string;
  /** Lo que se arregla SOLO (0.44.0, api lib/salud.ts): últimos 30 días. */
  automatico?: {
    regla: { fusionar: boolean; citas_duplicadas: boolean; cerrar_pasadas: boolean }; ultimo: string | null; errores: string[]; pendientes: number | null;
    fusionados: { fecha: string; ganador: string; ganador_nombre: string | null; perdedor: string; perdedor_nombre: string | null; motivo: string | null }[];
    citasQuitadas: { fecha: string; cita: string; paciente: string | null; patient_id: string | null; cuando: string | null }[];
    citasCerradas: { fecha: string; cita: string; paciente: string | null; patient_id: string | null; cuando: string | null; motivo: string | null }[];
  };
  decidir?: {
    error?: string;
    familias?: { telefono: string; personas: { id: string; nombre: string | null; alta: string }[]; parecido: boolean; nota: string;
      sugeridos?: { ganador: { id: string; nombre: string | null }; perdedor: { id: string; nombre: string | null }; razon?: string }[] }[];
    bloqueados?: { telefono: string; ganador: { id: string; nombre: string | null; alta?: string; citas?: number }; perdedor: { id: string; nombre: string | null; alta?: string; citas?: number }; razon: string; juntarAMano?: boolean; correos?: [string, string] }[];
  };
  pruebasProbables?: { patient_id: string; nombre: string; alta: string }[];
  citasPasadasSinCerrar?: number | null;
}

/** De qué está hecho el número. Un embudo pintado sobre pacientes duplicados no
 *  se ve roto: se ve bien y está mal. Este es el único lugar donde se nota. */
function SaludDatos() {
  // Juntar expedientes, "Revisar ahora", marcar cuentas de prueba (piden PIN) y el respaldo manual (migraciones, ventas,
  // gasto, importar de Halory) son de admin aunque Reglas y datos esté abierta (lib/permisos.ts del API).
  const { esAdmin } = usePermisos();
  const [d, setD] = useState<Salud | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [revisando, setRevisando] = useState(false);
  const cargar = () => api<Salud>('data-health').then(setD).catch((e) => setErr(e.message));
  useEffect(() => { cargar(); }, []);
  async function revisarAhora() {
    setRevisando(true);
    try { await api('data-health/fusiones', { method: 'POST' }); await cargar(); }
    catch (e: any) { avisar({ titulo: 'No se pudo revisar', mensaje: textoDeError(e), tipo: 'error' }); }
    finally { setRevisando(false); }
  }
  const [juntando, setJuntando] = useState<string | null>(null);
  async function juntar(b: { ganador: { id: string; nombre: string | null }; perdedor: { id: string; nombre: string | null } }) {
    const g = b.ganador.nombre || '(sin nombre)', p = b.perdedor.nombre || '(sin nombre)';
    const ok = await confirmar({
      titulo: '¿Son la misma persona?',
      mensaje: `Se queda «${g}» y recibe todo lo de «${p}»: citas, historial, la liga de Halory y su correo (los dos correos se conservan).\nEl otro expediente deja de salir y su liga lleva al que se queda.`,
      confirmar: 'Sí, juntar', cancelar: 'No, dejarlos así',
    });
    if (!ok) return;
    setJuntando(b.perdedor.id);
    try { await api('data-health/fusiones', { method: 'POST', body: JSON.stringify({ ganador: b.ganador.id, perdedor: b.perdedor.id }) }); await cargar(); }
    catch (e: any) { avisar({ titulo: 'No se juntaron', mensaje: textoDeError(e), tipo: 'error' }); }
    finally { setJuntando(null); }
  }
  async function marcarPrueba(id: string, nombre: string) {
    try { await api(`pruebas/${id}`, { method: 'PUT', body: JSON.stringify({ prueba: true, nota: 'Marcada desde Salud de datos' }) }); await cargar(); }
    catch (e: any) { avisar({ titulo: `No se pudo marcar a ${nombre}`, mensaje: textoDeError(e), tipo: 'error' }); }
  }

  const respaldo = !esAdmin ? (
    <p className="muted" style={{ fontSize: 12.5, margin: 0 }} data-solo-admin="salud">Juntar expedientes, marcar cuentas de prueba y el respaldo manual (migraciones, ventas, gasto de pauta e importar de Halory) los hace sólo una cuenta de admin.</p>
  ) : (
    <details className="card" style={{ padding: '12px 14px' }} data-respaldo-manual>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Respaldo manual (no hace falta usarlo)</summary>
      <div className="muted" style={{ fontSize: 13, margin: '6px 0 10px' }}>
        Todo esto ya pasa solo: las migraciones con cada actualización, las ventas las escribe recepción, el gasto de pauta llega de Halory y los leads de Halory los trae el reloj. Queda aquí por si algo se ve raro.
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        <Migraciones />
        <CargarVentas />
        <CargarGasto />
        <ImportarHalory />
      </div>
    </details>
  );

  if (err) return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="card" style={{ padding: 16, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{err}</div>
      {respaldo}
    </div>
  );
  if (!d) return <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Revisando…</div>;

  const t = d.telefonos;
  const a = d.automatico;
  const familias = d.decidir?.familias || [];
  const bloqueados = d.decidir?.bloqueados || [];
  const parecidos = familias.flatMap((f) => (f.sugeridos || []).map((x) => ({ ...x, telefono: f.telefono })));
  const fechaCorta = (x: string | null) => (x ? new Date(x).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '');
  const linkCss: React.CSSProperties = { color: 'var(--gold-dark)' };
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {a && (
        <div className="card" style={{ padding: '12px 14px' }} data-seccion="arreglado-solo">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ fontWeight: 600 }}>Lo que se arregló solo <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· últimos 30 días</span></div>
            {esAdmin && <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={revisarAhora} disabled={revisando} data-revisar-ahora>{revisando ? 'Revisando…' : 'Revisar ahora'}</button>}
          </div>
          <div className="muted" style={{ fontSize: 12.5, margin: '4px 0 10px' }}>
            Se revisa sola una vez por noche (y al momento con «Revisar ahora»): junta expedientes repetidos de la misma persona (mismo teléfono y mismo nombre, sin historia clínica ni dinero en el que se va), quita citas repetidas y cierra como completadas las citas pasadas en las que hubo evidencia de que la persona vino. {a.ultimo ? `Última revisión: ${new Date(a.ultimo).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}` : 'Todavía no corre.'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 10 }}>
            <Mini label="Expedientes juntados" n={a.fusionados.length} />
            <Mini label="Citas repetidas quitadas" n={a.citasQuitadas.length} />
            <Mini label="Citas pasadas cerradas" n={a.citasCerradas.length} />
            <Mini label="Por juntar (siguiente vuelta)" n={a.pendientes ?? 0} />
          </div>
          {!a.regla.fusionar || !a.regla.citas_duplicadas || !a.regla.cerrar_pasadas ? (
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>Apagado en esta clínica: {[!a.regla.fusionar && 'juntar expedientes', !a.regla.citas_duplicadas && 'quitar citas repetidas', !a.regla.cerrar_pasadas && 'cerrar citas pasadas'].filter(Boolean).join(', ')}.</div>
          ) : null}
          {a.fusionados.length > 0 && (
            <details style={{ marginTop: 10 }} data-lista="fusionados">
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>Expedientes juntados ({a.fusionados.length})</summary>
              {a.fusionados.map((x) => (
                <div key={x.perdedor} style={{ borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13 }}>
                  <Link to={`/paciente/${x.ganador}`} style={linkCss}>{x.ganador_nombre || '(sin nombre)'}</Link>
                  <span className="muted"> se quedó; </span>
                  <Link to={`/paciente/${x.perdedor}`} style={linkCss}>{x.perdedor_nombre || '(sin nombre)'}</Link>
                  <span className="muted"> se juntó con ése · {fechaCorta(x.fecha)}{x.motivo ? ` · ${x.motivo}` : ''}</span>
                </div>
              ))}
            </details>
          )}
          {a.citasQuitadas.length > 0 && (
            <details style={{ marginTop: 6 }} data-lista="citas-quitadas">
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>Citas repetidas quitadas ({a.citasQuitadas.length})</summary>
              {a.citasQuitadas.map((x) => (
                <div key={x.cita + x.fecha} style={{ borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13 }}>
                  {x.patient_id ? <Link to={`/paciente/${x.patient_id}`} style={linkCss}>{x.paciente || '(sin nombre)'}</Link> : <span>{x.paciente || '(sin nombre)'}</span>}
                  <span className="muted"> · cita del {fechaCorta(x.cuando)}: tenía otra igual a menos de 10 minutos · {fechaCorta(x.fecha)}</span>
                </div>
              ))}
            </details>
          )}
          {a.citasCerradas.length > 0 && (
            <details style={{ marginTop: 6 }} data-lista="citas-cerradas">
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>Citas pasadas cerradas como completadas ({a.citasCerradas.length})</summary>
              {a.citasCerradas.map((x) => (
                <div key={x.cita} style={{ borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13 }}>
                  {x.patient_id ? <Link to={`/paciente/${x.patient_id}`} style={linkCss}>{x.paciente || '(sin nombre)'}</Link> : <span>{x.paciente || '(sin nombre)'}</span>}
                  <span className="muted"> · cita del {fechaCorta(x.cuando)}{x.motivo ? `: ${x.motivo}` : ''}</span>
                </div>
              ))}
            </details>
          )}
          {a.errores.length > 0 && <div style={{ fontSize: 12, marginTop: 8, color: 'var(--warning)' }} data-errores-auto>No se pudo en la última vuelta: {a.errores.join(' · ')}</div>}
        </div>
      )}

      {typeof d.citasPasadasSinCerrar === 'number' && (
        <div className="card" style={{ padding: '12px 14px' }} data-seccion="pasadas-sin-cerrar">
          <div style={{ fontWeight: 600 }}>{d.citasPasadasSinCerrar.toLocaleString('es-MX')} {d.citasPasadasSinCerrar === 1 ? 'cita pasada sin cerrar' : 'citas pasadas sin cerrar'}</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            {d.citasPasadasSinCerrar === 0
              ? 'Todas las citas de días que ya pasaron tienen su estado.'
              : 'Nadie marcó si vino: siguen como programadas o confirmadas y ese día no hubo compra, consulta ni check-in de la persona. Casi todas son del histórico del CRM. No se cierran solas porque no hay con qué saber si vino.'}
          </div>
        </div>
      )}

      {(familias.length > 0 || bloqueados.length > 0) && (
        <div className="card" style={{ padding: '12px 14px' }} data-seccion="por-decidir">
          <div style={{ fontWeight: 600 }}>Lo que necesita a una persona</div>
          {bloqueados.length > 0 && (
            <>
              <div className="muted" style={{ fontSize: 12.5, margin: '4px 0 6px' }}>Mismo teléfono y mismo nombre, pero no se juntan solos: los dos tienen historia, uno es cuenta de prueba, o traen correos distintos. Revisa las dos fichas; si sólo los detienen los correos y es la misma persona, júntalos aquí.</div>
              {bloqueados.map((b) => (
                <div key={b.perdedor.id} style={{ borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }} data-bloqueado>
                  <div style={{ minWidth: 0 }}>
                    <span className="badge" style={{ marginRight: 8 }}>{b.telefono}</span>
                    <Link to={`/paciente/${b.ganador.id}`} style={linkCss}>{b.ganador.nombre || '(sin nombre)'}</Link>
                    <span className="muted"> · </span>
                    <Link to={`/paciente/${b.perdedor.id}`} style={linkCss}>{b.perdedor.nombre || '(sin nombre)'}</Link>
                    <div className="muted" style={{ fontSize: 12 }}>{b.razon}{b.correos ? `: ${b.correos.filter(Boolean).join(' y ')}` : ''}</div>
                  </div>
                  {esAdmin && b.juntarAMano && (
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} disabled={juntando === b.perdedor.id} onClick={() => juntar(b)} data-juntar>
                      {juntando === b.perdedor.id ? 'Juntando…' : 'Son la misma persona: juntar'}
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
          {parecidos.length > 0 && (
            <>
              <div className="muted" style={{ fontSize: 12.5, margin: '10px 0 6px' }}>Mismo teléfono y un nombre casi igual (una letra cambiada, un apellido de más). Puede ser la misma persona escrita distinto; solos no se juntan.</div>
              {parecidos.map((x) => (
                <div key={x.ganador.id + x.perdedor.id} style={{ borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }} data-parecido>
                  <div style={{ minWidth: 0 }}>
                    <span className="badge" style={{ marginRight: 8 }}>{x.telefono}</span>
                    <Link to={`/paciente/${x.ganador.id}`} style={linkCss}>{x.ganador.nombre || '(sin nombre)'}</Link>
                    <span className="muted"> · </span>
                    <Link to={`/paciente/${x.perdedor.id}`} style={linkCss}>{x.perdedor.nombre || '(sin nombre)'}</Link>
                    {x.razon && <div className="muted" style={{ fontSize: 12 }}>No se pueden juntar desde aquí: {x.razon}</div>}
                  </div>
                  {esAdmin && !x.razon && (
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} disabled={juntando === x.perdedor.id} onClick={() => juntar(x)} data-juntar>
                      {juntando === x.perdedor.id ? 'Juntando…' : 'Son la misma persona: juntar'}
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
          {familias.length > 0 && (
            <details style={{ marginTop: 8 }} data-lista="familias">
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>Comparten teléfono (no es error): {familias.length} {familias.length === 1 ? 'número' : 'números'}{familias.some((f) => f.parecido) ? ` · ${familias.filter((f) => f.parecido).length} con nombres parecidos` : ''}</summary>
              <div className="muted" style={{ fontSize: 12, margin: '4px 0' }}>Familia, pareja, el teléfono de casa: personas distintas con el mismo número. No se juntan. Si los nombres se parecen, puede ser la misma persona escrita distinto: revisa las fichas.</div>
              {familias.map((f) => (
                <div key={f.telefono + f.personas[0]?.id} style={{ borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13 }} data-familia={f.parecido ? 'parecido' : 'distinto'}>
                  <span className="badge" style={{ marginRight: 8 }}>{f.telefono}</span>
                  {f.personas.map((p, i) => (
                    <span key={p.id}>{i > 0 && <span className="muted"> · </span>}<Link to={`/paciente/${p.id}`} style={linkCss}>{p.nombre || '(sin nombre)'}</Link></span>
                  ))}
                  {f.parecido && <div style={{ fontSize: 12, color: 'var(--warning)' }}>{f.nota}</div>}
                </div>
              ))}
            </details>
          )}
        </div>
      )}

      {d.pruebasProbables && d.pruebasProbables.length > 0 && (
        <div className="card" style={{ padding: '12px 14px' }} data-seccion="pruebas-probables">
          <div style={{ fontWeight: 600 }}>Parecen cuentas de prueba ({d.pruebasProbables.length})</div>
          <div className="muted" style={{ fontSize: 12.5, margin: '4px 0 6px' }}>Su nombre dice "prueba", "test", "borrar" o "demo" y no tienen compras. No se marcan solas: una cuenta de prueba sale de todas las métricas.</div>
          {d.pruebasProbables.map((p) => (
            <div key={p.patient_id} style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13 }} data-probable>
              <span style={{ minWidth: 0 }}><Link to={`/paciente/${p.patient_id}`} style={linkCss}>{p.nombre}</Link> <span className="muted">· alta {p.alta}</span></span>
              {esAdmin && <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => marcarPrueba(p.patient_id, p.nombre)} data-marcar-prueba>Marcar como prueba</button>}
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: '12px 14px' }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Qué hay en la base</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 130px),1fr))', gap: 10 }}>
          {d.volumen.map((v) => (
            <div key={v.t} style={{ background: 'var(--bg-alt)', borderRadius: 10, padding: '8px 10px' }}>
              <div className="muted" style={{ fontSize: 12 }}>{v.t}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{v.n.toLocaleString('es-MX')}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, fontSize: 13 }}>
          {d.rangosDeFecha.map((r) => (
            <div key={r.campo} className="muted">
              <strong style={{ color: 'var(--ink)' }}>{r.campo}:</strong> {r.desde || '—'} → {r.hasta || '—'} ({r.n.toLocaleString('es-MX')} con fecha)
            </div>
          ))}
        </div>
        <div className="muted" style={{ fontSize: 13, marginTop: 10 }}>
          Teléfonos: {t.conTelefonoUsable.toLocaleString('es-MX')} números usables · {t.numerosCompartidos} compartidos ({t.pacientesAfectados} expedientes) · sin teléfono {t.sinTelefono} · ilegible {t.ilegible}.
        </div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Origen de las citas: {d.origenDeCitas.map((o) => `${o.origen}: ${o.n.toLocaleString('es-MX')}`).join(' · ')}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          {d.halory.pacientesCreadosDesdeHalory.toLocaleString('es-MX')} expedientes vinculados con Halory. {d.halory.nota}
        </div>
      </div>

      {d.nombresRepetidos.length > 0 && (
        <details className="card" style={{ padding: '12px 14px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Nombres idénticos ({d.nombresRepetidos.length})</summary>
          <div className="muted" style={{ fontSize: 12, margin: '6px 0 8px' }}>
            Dos personas pueden llamarse igual. Con el mismo teléfono y sin historia en uno de los dos, se juntan solas (arriba); aquí salen todos los nombres repetidos, tengan o no el mismo teléfono.
          </div>
          {d.nombresRepetidos.map((n) => (
            <div key={n.nombre} style={{ borderTop: '1px solid var(--border)', padding: '8px 0' }}>
              <div style={{ fontWeight: 500, fontSize: 14, textTransform: 'capitalize' }}>{n.nombre}</div>
              {n.casos.map((c) => (
                <div key={c.id} className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                  <Link to={`/paciente/${c.id}`} style={linkCss}>ver ficha</Link>
                  {' · '}alta {c.alta} · {c.status || 'sin etapa'} · {c.asesora || 'sin asignar'} · {c.origen || 'sin origen'} · {c.citas} cita{c.citas === 1 ? '' : 's'}
                </div>
              ))}
            </div>
          ))}
        </details>
      )}

      {respaldo}
    </div>
  );
}

/** Las migraciones se aplican SOLAS en cada deploy (lib/automigrar en la API,
 *  con candado entre instancias). Este botón queda como RESPALDO: re-corre todo
 *  el catálogo, para forzar cuando algo se vea raro. */
function Migraciones() {
  const [estado, setEstado] = useState<'idle' | 'corriendo' | 'listo'>('idle');
  const [res, setRes] = useState<any>(null);
  const [confirmar, setConfirmar] = useState(false);

  async function aplicar() {
    setEstado('corriendo');
    try { setRes(await api('migrate', { method: 'POST', body: JSON.stringify({ confirmar: 'aplicar' }) })); }
    catch (e: any) { setRes({ resumen: e.message }); }
    finally { setEstado('listo'); setConfirmar(false); }
  }

  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div style={{ fontWeight: 600 }}>Migraciones de base de datos</div>
      <div className="muted" style={{ fontSize: 13, margin: '4px 0 10px' }}>
        Se aplican <strong>solas</strong> con cada actualización de la app — ya no hay que venir aquí.
        Este botón es el respaldo por si algo se ve raro: re-corre todo el catálogo. Aditivas e
        idempotentes: crean tablas del HUB y no tocan pacientes, citas ni nada de DraIA.
      </div>
      {!confirmar
        ? <button className="btn-ghost" onClick={() => setConfirmar(true)} disabled={estado === 'corriendo'}>Re-aplicar migraciones (respaldo)</button>
        : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13 }}>¿Aplicar ahora?</span>
            <button className="btn-primary" onClick={aplicar} disabled={estado === 'corriendo'}>{estado === 'corriendo' ? 'Aplicando…' : 'Sí, aplicar'}</button>
            <button className="btn-ghost" onClick={() => setConfirmar(false)}>Cancelar</button>
          </div>
        )}
      {res && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <strong>{res.resumen}</strong>
          {(res.resultados || []).map((r: any) => (
            <div key={r.nombre} className="muted" style={{ fontSize: 12, marginTop: 2, color: r.ok ? undefined : 'var(--warning)' }}>
              {r.ok ? '✓' : '✕'} {r.nombre} — {r.detalle}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WindowRow({ w }: { w: Window }) {
  const [days, setDays] = useState(w.window_days);
  const [activo, setActivo] = useState(w.activo);
  const [state, setState] = useState<'idle' | 'saving' | 'ok'>('idle');
  const dirty = days !== w.window_days || activo !== w.activo;

  async function save() {
    setState('saving');
    try { await api('crm/window', { method: 'PUT', body: JSON.stringify({ window_id: w.window_id, window_days: days, activo }) }); w.window_days = days; w.activo = activo; setState('ok'); setTimeout(() => setState('idle'), 1500); }
    catch (e: any) { setState('idle'); avisar({ titulo: `No se pudo guardar «${w.label}»`, mensaje: textoDeError(e), tipo: 'error' }); }
  }

  return (
    // En celular el nombre va arriba y los controles en su propio renglón (antes se salían 130 px a la derecha).
    <div className="card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px 12px', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{w.label}</div>
        <div className="muted" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>coincide con: “{w.match_pattern}”</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value || '0', 10))} style={{ width: 90 }} aria-label={`Días de ${w.label}`} />
        <span className="muted" style={{ fontSize: 13 }}>días</span>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} style={{ width: 'auto', accentColor: '#8b7355' }} /> activo</label>
        {/* "Guardar" sólo en la ventana que cambió (antes: un botón por renglón, los once a la vista y casi siempre apagados). */}
        {(dirty || state !== 'idle') && (
          <button className="btn-primary" onClick={save} disabled={!dirty || state === 'saving'} data-guardar-ventana={w.label} style={{ padding: '7px 16px' }}>
            {state === 'saving' ? 'Guardando…' : state === 'ok' ? 'Guardado' : 'Guardar'}</button>
        )}
      </div>
    </div>
  );
}


/* ==========================================================================
 * CARGA DEL DINERO REAL
 *
 * Hasta ahora toda la analítica de ingreso era una ESTIMACIÓN a precio de lista
 * del catálogo: en la base clínica no hay un solo pago capturado. El dinero de
 * verdad vive en un Excel que se pasa por WhatsApp, y por eso la pregunta que
 * más importa —¿la pauta se paga sola?— no tenía dónde contestarse.
 *
 * El archivo se lee AQUÍ, en el navegador: trae nombres y teléfonos, y así al
 * servidor sólo viajan las columnas que la carga necesita.
 * ========================================================================== */

/** Formatea pesos sin decimales: en un tablero, los centavos sólo estorban. */
const mx = (n: number) => '$' + Math.round(n).toLocaleString('es-MX');

/** Carga del corte de ventas del CRM. */
function CargarVentas() {
  const [file, setFile] = useState<File | null>(null);
  const [filas, setFilas] = useState<any[] | null>(null);
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState<'' | 'leyendo' | 'previa' | 'real'>('');
  const [confirmar, setConfirmar] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function elegir(f: File) {
    setFile(f); setFilas(null); setRes(null); setErr(null); setConfirmar(false); setBusy('leyendo');
    try {
      const { leerHoja, columna, aFecha } = await import('../lib/hoja');
      const { headers, filas: crudas } = await leerHoja(f);
      const c = {
        fecha: columna(headers, 'fecha'),
        medico: columna(headers, 'nombre del medico', 'medico', 'doctor'),
        concepto: columna(headers, 'procedimiento / tratamiento', 'procedimiento', 'tratamiento'),
        paciente: columna(headers, 'paciente', 'nombre del paciente'),
        monto: columna(headers, 'monto', 'importe', 'total'),
        origen: columna(headers, 'origen'),
        contacto: columna(headers, 'contacto', 'telefono', 'celular'),
        ad: columna(headers, 'ad que convirtio', 'anuncio'),
        adset: columna(headers, 'conjunto de anuncios'),
        campana: columna(headers, 'campana'),
        fechaMeta: columna(headers, 'fecha click/view meta', 'fecha click'),
      };
      // Sin fecha o sin monto no hay venta que cargar. Vale más decirlo aquí,
      // con los encabezados a la vista, que mandar 236 filas al servidor para
      // que las descarte todas y devuelva un cero que parece un dato.
      if (c.fecha < 0 || c.monto < 0) {
        throw new Error(`No encontré las columnas "Fecha" y "Monto". Encabezados leídos: ${headers.filter(Boolean).join(' · ')}`);
      }
      const g = (r: string[], i: number) => (i >= 0 ? (r[i] || '').trim() : '');
      setFilas(crudas.map((r) => ({
        fecha: aFecha(g(r, c.fecha)), medico: g(r, c.medico), concepto: g(r, c.concepto),
        paciente: g(r, c.paciente), monto: g(r, c.monto), origen: g(r, c.origen),
        contacto: g(r, c.contacto), ad: g(r, c.ad), adset: g(r, c.adset),
        campana: g(r, c.campana), fechaMeta: aFecha(g(r, c.fechaMeta)),
      })));
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(''); }
  }

  async function enviar(real: boolean) {
    if (!filas) return;
    setBusy(real ? 'real' : 'previa'); setConfirmar(false); setErr(null);
    try { setRes(await api(`dinero/compras${real ? '' : '?dryRun=true'}`, { method: 'POST', body: JSON.stringify({ filas }) })); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(''); }
  }

  const r = res?.resultado;
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div style={{ fontWeight: 600 }}>Cargar ventas del CRM</div>
      <div className="muted" style={{ fontSize: 13, margin: '4px 0 10px' }}>
        El corte de cobros (.xlsx o .csv). Con esto el ingreso deja de ser una estimación a precio de lista
        y pasa a ser lo que de verdad se cobró. Se puede subir cada mes: <strong>volver a subir el mismo archivo no duplica nada</strong>.
      </div>

      <input type="file" accept=".xlsx,.csv" onChange={(e) => e.target.files?.[0] && elegir(e.target.files[0])}
             style={{ fontSize: 13 }} disabled={!!busy} />

      {busy === 'leyendo' && <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>Leyendo el archivo…</div>}
      {err && <div style={{ marginTop: 10, color: 'var(--warning)', fontSize: 13 }}>{err}</div>}

      {filas && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13 }}>
            <strong>{file?.name}</strong> — {filas.length} líneas leídas.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button className="btn-ghost" onClick={() => enviar(false)} disabled={!!busy}>
              {busy === 'previa' ? 'Calculando…' : 'Ver qué pasaría (no escribe)'}
            </button>
            {res && !res.dryRun ? null : !confirmar
              ? <button className="btn-ghost" onClick={() => setConfirmar(true)} disabled={!!busy || !res}>Cargar de verdad</button>
              : (
                <>
                  <button className="btn-primary" onClick={() => enviar(true)} disabled={!!busy}>
                    {busy === 'real' ? 'Cargando…' : 'Sí, cargar'}
                  </button>
                  <button className="btn-ghost" onClick={() => setConfirmar(false)}>Cancelar</button>
                </>
              )}
          </div>
        </div>
      )}

      {r && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            {res.dryRun ? 'Vista previa — no se escribió nada' : 'Carga aplicada'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))', gap: 8 }}>
            <MiniTxt label="Ingreso del archivo" v={mx(r.montoTotal)} />
            <Mini label="Ventas válidas" n={r.validas} />
            <Mini label={res.dryRun ? 'Se cargarían' : 'Cargadas'} n={res.dryRun ? r.seInsertarian : r.insertadas} />
            <Mini label="Ya estaban" n={res.dryRun ? r.yaCargadas : r.yaEstaban} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))', gap: 8, marginTop: 8 }}>
            <Mini label="Empate por teléfono" n={r.porTelefono} />
            <Mini label="Empate por nombre" n={r.porNombre} />
            <Mini label="Sin paciente" n={r.sinEmpate} warn={r.sinEmpate > r.validas / 2} />
            <Mini label="Con anuncio" n={r.conAtribucion} />
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.55 }}>
            {r.descartadas > 0 && <>Se descartaron <strong>{r.descartadas}</strong> líneas sin fecha o con monto 0 (el archivo trae renglones separadores). </>}
            De <strong>{mx(r.montoTotal)}</strong>, <strong>{mx(r.montoEmpatado)}</strong> quedan ligados a un expediente.
            El resto se cuenta igual en el ingreso: el dinero entró aunque el teléfono no empate.
            {' '}{res.nota}
          </div>
        </div>
      )}
    </div>
  );
}

/** Carga del gasto de pauta (export de Ads Manager, nivel anuncio). */
function CargarGasto() {
  const [file, setFile] = useState<File | null>(null);
  const [filas, setFilas] = useState<any[] | null>(null);
  const [repetidas, setRepetidas] = useState(0);
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState<'' | 'leyendo' | 'previa' | 'real'>('');
  const [confirmar, setConfirmar] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function elegir(f: File) {
    setFile(f); setFilas(null); setRes(null); setErr(null); setConfirmar(false); setRepetidas(0); setBusy('leyendo');
    try {
      const { leerHoja, columna, aFecha } = await import('../lib/hoja');
      const { headers, filas: crudas } = await leerHoja(f);
      const c = {
        fecha: columna(headers, 'inicio del informe', 'dia', 'fecha'),
        ad: columna(headers, 'nombre del anuncio'),
        adset: columna(headers, 'nombre del conjunto de anuncios'),
        campana: columna(headers, 'nombre de la campana'),
        gasto: columna(headers, 'importe gastado', 'amount spent'),
        impresiones: columna(headers, 'impresiones'),
        alcance: columna(headers, 'alcance'),
        resultados: columna(headers, 'resultados'),
      };
      if (c.fecha < 0 || c.ad < 0 || c.gasto < 0) {
        throw new Error(`No encontré "Inicio del informe", "Nombre del anuncio" o "Importe gastado". Encabezados leídos: ${headers.filter(Boolean).slice(0, 12).join(' · ')}…`);
      }
      const g = (r: string[], i: number) => (i >= 0 ? (r[i] || '').trim() : '');
      const leidas = crudas.map((r) => ({
        fecha: aFecha(g(r, c.fecha)), ad: g(r, c.ad), adset: g(r, c.adset), campana: g(r, c.campana),
        gasto: g(r, c.gasto), impresiones: g(r, c.impresiones), alcance: g(r, c.alcance), resultados: g(r, c.resultados),
      }));

      // AGRUPAR AQUÍ, ANTES DE PARTIR EN TANDAS. El servidor también agrupa,
      // pero sólo dentro de la tanda que le llega: si el mismo anuncio-día
      // cayera en dos tandas, la segunda REEMPLAZARÍA a la primera y ese gasto
      // se perdería en silencio. Agrupar el archivo completo antes garantiza
      // que cada llave viaje en una sola tanda. Un export real traía 618
      // llaves repetidas de 19,570 filas, así que no es hipotético.
      const mapa = new Map<string, any>();
      for (const f of leidas) {
        const k = `${f.fecha}|${f.ad}|${f.adset}`;
        const p = mapa.get(k);
        if (!p) { mapa.set(k, { ...f }); continue; }
        const suma = (a: string, b: string) => String((Number(a) || 0) + (Number(b) || 0));
        p.gasto = suma(p.gasto, f.gasto);
        p.impresiones = suma(p.impresiones, f.impresiones);
        p.alcance = suma(p.alcance, f.alcance);
        p.resultados = suma(p.resultados, f.resultados);
      }
      setFilas([...mapa.values()]);
      setRepetidas(leidas.length - mapa.size);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(''); }
  }

  // El export de Meta trae una fila por anuncio y por día: un trimestre son
  // miles. Se manda por tandas para no chocar con el límite del servidor.
  // Es seguro partirlo porque el gasto se REEMPLAZA por día+anuncio, no se
  // suma — el orden de las tandas da igual y repetir una no infla nada.
  async function enviar(real: boolean) {
    if (!filas) return;
    setBusy(real ? 'real' : 'previa'); setConfirmar(false); setErr(null);
    try {
      const TANDA = 2000;
      const acc: any = { filas: 0, validas: 0, descartadas: 0, gastoTotal: 0, anuncios: 0, escritas: 0, desde: '', hasta: '' };
      for (let i = 0; i < filas.length; i += TANDA) {
        const parte: any = await api(`dinero/gasto${real ? '' : '?dryRun=true'}`, {
          method: 'POST', body: JSON.stringify({ filas: filas.slice(i, i + TANDA) }),
        });
        const p = parte.resultado;
        acc.filas += p.filas; acc.validas += p.validas; acc.descartadas += p.descartadas;
        acc.gastoTotal += p.gastoTotal; acc.escritas += p.escritas || 0;
        // Los anuncios distintos se cuentan por tanda, así que sumarlos daría
        // de más. Se queda el máximo, que es un piso honesto, y se dice.
        acc.anuncios = Math.max(acc.anuncios, p.anuncios);
        if (p.desde && (!acc.desde || p.desde < acc.desde)) acc.desde = p.desde;
        if (p.hasta && (!acc.hasta || p.hasta > acc.hasta)) acc.hasta = p.hasta;
      }
      setRes({ dryRun: !real, resultado: acc, tandas: Math.ceil(filas.length / TANDA) });
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(''); }
  }

  const r = res?.resultado;
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div style={{ fontWeight: 600 }}>Cargar gasto de pauta (Meta)</div>
      <div className="muted" style={{ fontSize: 13, margin: '4px 0 10px' }}>
        <strong>Con la llave de Halory ya no hace falta:</strong> el reloj trae el gasto por anuncio y día desde la cuenta
        publicitaria que Halory tiene conectada (Halory → Marketing → Pauta): cada hora los últimos 2 días y cada noche los últimos 28. Este archivo queda de respaldo, o para
        fechas de antes de conectarla: el export de Ads Manager a nivel anuncio (.csv o .xlsx), una fila por anuncio y día.
        Las fechas que ya estén cargadas <strong>se reemplazan</strong> — Meta reajusta el gasto durante varios días.
      </div>

      <input type="file" accept=".xlsx,.csv" onChange={(e) => e.target.files?.[0] && elegir(e.target.files[0])}
             style={{ fontSize: 13 }} disabled={!!busy} />

      {busy === 'leyendo' && <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>Leyendo el archivo…</div>}
      {err && <div style={{ marginTop: 10, color: 'var(--warning)', fontSize: 13 }}>{err}</div>}

      {filas && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13 }}>
            <strong>{file?.name}</strong> — {filas.length.toLocaleString('es-MX')} anuncio-día.
            {repetidas > 0 && <span className="muted"> ({repetidas.toLocaleString('es-MX')} renglones repetidos del export se sumaron en uno.)</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button className="btn-ghost" onClick={() => enviar(false)} disabled={!!busy}>
              {busy === 'previa' ? 'Calculando…' : 'Ver qué pasaría (no escribe)'}
            </button>
            {res && !res.dryRun ? null : !confirmar
              ? <button className="btn-ghost" onClick={() => setConfirmar(true)} disabled={!!busy || !res}>Cargar de verdad</button>
              : (
                <>
                  <button className="btn-primary" onClick={() => enviar(true)} disabled={!!busy}>
                    {busy === 'real' ? 'Cargando…' : 'Sí, cargar'}
                  </button>
                  <button className="btn-ghost" onClick={() => setConfirmar(false)}>Cancelar</button>
                </>
              )}
          </div>
        </div>
      )}

      {r && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            {res.dryRun ? 'Vista previa — no se escribió nada' : 'Carga aplicada'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))', gap: 8 }}>
            <MiniTxt label="Gasto del archivo" v={mx(r.gastoTotal)} />
            <Mini label="Filas válidas" n={r.validas} />
            <MiniTxt label="Anuncios (al menos)" v={String(r.anuncios)} />
            <MiniTxt label="Periodo" v={r.desde ? `${r.desde} → ${r.hasta}` : '—'} />
          </div>
          {!res.dryRun && <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>{r.escritas.toLocaleString('es-MX')} filas escritas en {res.tandas} tanda(s).</div>}
        </div>
      )}
    </div>
  );
}

/** Importación de leads desde Halory, en dos tiempos: primero ver qué pasaría,
 *  luego aplicarlo. Una importación mete filas en la base clínica y mueve todas
 *  las conversiones del embudo; verlo antes es la diferencia entre una decisión
 *  y una apuesta. */
function ImportarHalory() {
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState<'' | 'previa' | 'real'>('');
  const [confirmar, setConfirmar] = useState(false);

  async function correr(real: boolean) {
    setBusy(real ? 'real' : 'previa');
    setConfirmar(false);
    try { setRes(await api(`halory/import${real ? '' : '?dryRun=true'}`, { method: 'POST', body: '{}' })); }
    catch (e: any) { setRes({ error: e.message }); }
    finally { setBusy(''); }
  }

  const r = res?.resultado;
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div style={{ fontWeight: 600 }}>Importar leads desde Halory</div>
      <div className="muted" style={{ fontSize: 13, margin: '4px 0 10px' }}>
        Con la llave de Halory esto pasa solo: cada lead nuevo llega al momento por el aviso de Halory, y la revisión de
        cada noche (o «Revisar ahora» en Asignación de leads) trae los que falten. Esto queda para una carga completa a mano. Sólo se traen los leads <strong>con teléfono</strong>
        (los de Messenger sin teléfono se quedan en Halory, donde sí se pueden atender) y, si el expediente ya existe
        aquí, no se toca nada: sólo se anota la equivalencia.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn-ghost" onClick={() => correr(false)} disabled={!!busy}>
          {busy === 'previa' ? 'Calculando…' : 'Ver qué pasaría (no escribe)'}
        </button>
        {!confirmar
          ? <button className="btn-ghost" onClick={() => setConfirmar(true)} disabled={!!busy}>Importar de verdad</button>
          : (
            <>
              <button className="btn-primary" onClick={() => correr(true)} disabled={!!busy}>
                {busy === 'real' ? 'Importando…' : 'Sí, importar'}
              </button>
              <button className="btn-ghost" onClick={() => setConfirmar(false)}>Cancelar</button>
            </>
          )}
      </div>

      {res?.error && <div style={{ marginTop: 10, color: 'var(--warning)', fontSize: 13 }}>{res.error}</div>}
      {r && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            {res.dryRun ? 'Vista previa — no se escribió nada' : 'Importación aplicada'}
            <span className="muted" style={{ fontWeight: 400 }}> · {res.halory?.leidos} leads leídos de Halory</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))', gap: 8 }}>
            <Mini label={res.dryRun ? 'Se crearían' : 'Creados'} n={r.creados} />
            <Mini label={res.dryRun ? 'Se vincularían' : 'Vinculados'} n={r.vinculados} />
            <Mini label="Ya estaban" n={r.yaEstaban} />
            <Mini label="Teléfono ambiguo" n={r.ambiguos} warn={r.ambiguos > 0} />
            <Mini label="Sin teléfono (se quedan)" n={r.sinTelefono} />
          </div>
          {!res.dryRun && <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>Avisados a Halory con su id de DTB: {r.avisadosAHalory}</div>}
          {r.errores?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--warning)' }}>
              {r.errores.length} error(es): {r.errores.slice(0, 3).join(' · ')}
            </div>
          )}
          {res.empate && (
            <div style={{ marginTop: 10, padding: '9px 11px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.5,
                          background: res.empate.conTelefonoUsable < res.empate.pacientesEnDTB * 0.5 ? 'var(--warning-bg)' : 'var(--bg-alt)',
                          color: res.empate.conTelefonoUsable < res.empate.pacientesEnDTB * 0.5 ? 'var(--warning)' : undefined }}>
              <strong>¿Es confiable el empate?</strong> {res.empate.lectura}
            </div>
          )}
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Quien atiende lo decide Halory. {res.nota}
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, n, warn }: { label: string; n: number; warn?: boolean }) {
  return (
    <div style={{ background: 'var(--bg-alt)', borderRadius: 10, padding: '8px 10px' }}>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, color: warn && n > 0 ? 'var(--warning)' : undefined }}>{n ?? 0}</div>
    </div>
  );
}

function MiniTxt({ label, v }: { label: string; v: string }) {
  return (
    <div style={{ background: 'var(--bg-alt)', borderRadius: 10, padding: '8px 10px' }}>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700 }}>{v}</div>
    </div>
  );
}
